import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonGrid from "../CommonGrid";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import ConfirmModal from "../../ItemProperties/Forms/FieldDataValidation/ConfirmModal";
import LightTooltip from "../../../commonComponents/LightTooltip";
import NtwAlert from "../../ContextMenu/NewTemplateWizard/NtwContentOptions/NtwAlert";
import { Button, Checkbox, CircularProgress, FormControlLabel } from "@mui/material";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { newTemplateActions } from "../../../redux/slices/newTemplateSlice";

function TradingPartnerKeys({ setTPKeyScreen, tpName }) {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.queryParams);  
  const userPermissions = useSelector((state) => state.userPermissions.permissions);
  const dispatch = useDispatch();
  
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [tpKeysLoading, setTPKeysLoading] = useState(false);
  const [error, setError] = useState("");
  const [severity, setSeverity] = useState('success');
  const [disableApplyBtn, setDisableApplyBtn] = useState(true);
  const [keyData, setKeyData] = useState({ data: { trading_partner_key_details: [] } });
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [originalKeys, setOriginalKeys] = useState(new Map()); 
  const [renamedKeys, setRenamedKeys] = useState([]); 

  const getGridAPI = (api) => {
    setGridApi(api);
  };

  const CustomCellRenderer = (info) => {
    const { value, api, data, colDef } = info;
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState("");
    const [rows, setRows] = useState([]);
    const [rowIndex, setRowIndex] = useState(null);

    // Gather all row data when the component mounts or updates
    useEffect(() => {
      if (api) {
        let tempRows = [];
        api.forEachNode((node) => {
          tempRows.push(node.data);
        });
        setRows(tempRows);
      }
    }, [api]);

    const yesHandler = async () => {
      const keyName = rows[rowIndex]?.key;
      const deleteResponse = await deleteTPKey(keyName);

      if (deleteResponse) {
        const updatedRows = [...rows];
        updatedRows.splice(rowIndex, 1);
        // Adjust IDs after deletion
        updatedRows.forEach((row, index) => {
          row.id = index;
        });
        setRows(updatedRows);
        setRowData(updatedRows);
        api.setGridOption('rowData', updatedRows);
        removeNonExistentKeys(updatedRows);
      }
    };

    // Handle the delete action
    const handleDeleteAction = (e, rowData) => {
      const index = rowData.id;
      const tpKeyName = rows[index]?.key;
      setRowIndex(index);
      setDeleteMessage(`Are you sure you want to delete ${tpKeyName} Trading Partner Key?`);
      setOpenDeleteModal(true);
    };
    
    const renderValue = (cellValue) => {
      if (!cellValue || /^\s*$/.test(cellValue)) {
        return <span style={{ color: "#0000008A" }}>{t("ADD_TP_KEYS")}</span>;
      }
      return (
        <div
          className="truncated-text"
          title={cellValue}
          style={{ whiteSpace: 'pre-wrap',display:'flex',alignItems:'center',justifyContent:'flex-start',lineHeight:'normal'}} // Preserves spaces visually
        >
          {cellValue}
        </div>
      );
    };
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "2rem",
        }}>
        {renderValue(value)}
        {colDef.editable && data?.key && data?.key !== "" ? (
          <div className="flex align-center">
            <LightTooltip title={"Delete Trading Partner Key"} arrow>
              <span
                style={{ cursor: "pointer" }}
                className="DELETE_MUI"
                onClick={(e) => handleDeleteAction(e, data)}></span>
            </LightTooltip>
          </div>
        ) : (
          ""
        )}
        <ConfirmModal
          openModal={openDeleteModal}
          message={deleteMessage}
          handleClose={setOpenDeleteModal}
          yesHandler={yesHandler}
          noHandler={() => setOpenDeleteModal(false)}
        />
      </div>
    );
  };
  // Searchable and Unique keys component
  const CustomCheckBoxEditor = (props) => {
    const { value, colDef } = props;
    const [checked, setChecked] = useState(value === 'Y' || value === true);
  
    useEffect(() => {
      setChecked(value === 'Y' || value === true);
    }, [value]);
  
    const handleChange = (event, newChecked) => {
      if (!colDef.editable) return;
        setChecked(newChecked);
        props.setValue(newChecked ? 'Y' : 'N');
    };
  
    return (
      <FormControlLabel
        sx={{ height: "30px" }}
        className={"width-fit-content"}
        control={
          <Checkbox
            name={colDef.field}
            size="small"
            checked={checked}
            onChange={handleChange}
            disabled={!colDef.editable}
            style={{ paddingLeft: '10px' }}
          />
        }
      />
    );
  };
  const gridOptions = {
    defaultColDef: {
      resizable: true, 
    },
    columnDefs: [
      {
        headerName: t("COL_SEARCHABLE"),
        field: "searchable",
        editable: userPermissions.includes('TRADING_PARTNER_RIGHTS'),
        cellRenderer: (info) => CustomCheckBoxEditor(info),
        minWidth:100,
        maxWidth:100,
        flex:1
      },
      {
        headerName: t("COL_UNIQUE"),
        field: "unique",
        editable: userPermissions.includes('TRADING_PARTNER_RIGHTS'),
        cellRenderer: (info) => CustomCheckBoxEditor(info),
        minWidth:100,
        maxWidth:100,
        flex:1
      },
      {
        addNewControl: userPermissions.includes('TRADING_PARTNER_RIGHTS'),
        addControlLabel: t("ADD_TP_KEYS"),
        headerName: t("COL_KEY_NAME"),
        field: "key",
        editable: userPermissions.includes('TRADING_PARTNER_RIGHTS'),
        cellRenderer: CustomCellRenderer,
        minWidth:600,
        // autoHeight:true,
        flex:3       
      },
    
    ],
    getRowHeight: (params) => {
      const keyValue = params.data?.key || ""; 
      const baseHeight = 32; 
      const contentLength = keyValue.length; 

      return contentLength > 50 || keyValue.includes("\n") ? baseHeight * 1.5 : baseHeight;
    },
  };

  const checkDuplicateKey = (id, value, gridApi) => {
    if (!value) return false;
    let duplicateFound = false;
    gridApi?.forEachNode((node) => {
      if (node?.data?.id !== id && node?.data?.key?.toLowerCase() === value?.toLowerCase()) duplicateFound = true;
    });
    return duplicateFound;
  };

  // Remove non-existent keys from keyData based on updated rows
  const removeNonExistentKeys = (updatedRows) => {
    // Extract existing keys from updatedRows
    const existingKeys = new Set(updatedRows.map(row => row.key).filter(key => key !== null && key !== undefined && key.trim() !== ''));

    // Filter trading_partner_key_details to only include existing keys
    const filteredKeyDetails = keyData.data.trading_partner_key_details.filter(item => 
      item?.key && existingKeys.has(item.key)
    );

    setKeyData({
      data: {
        trading_partner_key_details: filteredKeyDetails
      }
    });

    // Rebuild originalKeys map based on updated rows (IDs may have changed after deletion)
    // Match by key value since IDs are renumbered
    const updatedOriginalKeys = new Map();
    updatedRows.forEach(row => {
      if (row.key && row.key.trim() !== "") {
        // Check if this key was renamed
        const renameItem = renamedKeys.find(r => r.newKey === row.key);
        if (renameItem) {
          // This is a renamed key, use the old key as original
          updatedOriginalKeys.set(row.id, renameItem.oldKey);
        } else {
          // Find original key by matching key value (since IDs may have changed)
          let foundOriginalKey = row.key; // Default to current key (new key)
          for (const originalKey of originalKeys.values()) {
            if (originalKey === row.key) {
              foundOriginalKey = originalKey;
              break;
            }
          }
          updatedOriginalKeys.set(row.id, foundOriginalKey);
        }
      }
    });
    setOriginalKeys(updatedOriginalKeys);
    
    // Update renamedKeys with new IDs after deletion (match by newKey value)
    setRenamedKeys(prev => {
      const updatedRenamedKeys = [];
      prev.forEach(renameItem => {
        const matchingRow = updatedRows.find(row => row.key === renameItem.newKey);
        if (matchingRow) {
          updatedRenamedKeys.push({
            ...renameItem,
            id: matchingRow.id
          });
        }
      });
      return updatedRenamedKeys;
    });

    // Disable Apply button if no keys are present
    if (filteredKeyDetails.length === 0) setDisableApplyBtn(true);
  };

  const transformData = (
  inputArray,
  existingData = { data: { trading_partner_key_details: [] } }
) => {
  // Deep clone only what we mutate
  const result = {
    ...existingData,
    data: {
      ...existingData.data,
      trading_partner_key_details: [
        ...existingData.data.trading_partner_key_details
      ]
    }
  };

  inputArray.forEach(item => {
    const existingIndex = result.data.trading_partner_key_details.findIndex(
      detail => detail.id === item.id
    );

    if (existingIndex !== -1) {
      // UPDATE existing row
      result.data.trading_partner_key_details[existingIndex] = {
        ...result.data.trading_partner_key_details[existingIndex],
        key: item.key,
        unique: item.unique || "N",
        searchable: item.searchable || "N"
      };
    } else {
      // ADD only if truly new
      const maxId =
        result.data.trading_partner_key_details.length > 0
          ? Math.max(
              ...result.data.trading_partner_key_details.map(d => d.id || 0)
            )
          : 0;

      result.data.trading_partner_key_details.push({
        id: maxId + 1,
        key: item.key,
        unique: item.unique || "N",
        searchable: item.searchable || "N"
      });
    }
  });

  return result;
};


  // Updated handleCellValueChanged function
  const handleCellValueChanged = (event) => {
    const { column, data, oldValue, api, rowIndex } = event;
    const colId = column.colId;
    const newValue = data[colId];

    if (newValue === oldValue) return;

    if (data.key && data.key.trim() !== "") setDisableApplyBtn(false);

    if (colId === "key") {
      const id = data.id;
      if (newValue.length > 200) {
        const truncatedValue = newValue.slice(0, 200);
        // Update the value in the grid row data
        data[colId] = truncatedValue;
        setErrorModal(true);
        setErrorMessage("Allowed characters are 200.Excess characters have been truncated.");
        gridApi.applyTransaction({ update: [data] });
      }
      const isDuplicateKey = checkDuplicateKey(id, newValue, api);

      if (isDuplicateKey) {
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        setErrorModal(true);
        setErrorMessage(t("BOX_NAME_ALREADY_EXISTS"));
        rowNode.setDataValue(colId, oldValue || "");
        // setDisableApplyBtn(true);
        return;
      }

      // Detect if this is a rename 
      const originalKey = originalKeys.get(id);
      if (originalKey && originalKey.trim() !== "" && newValue.trim() !== "" && 
          originalKey.trim() !== newValue.trim()) {
        
        setRenamedKeys(prev => {
          const filtered = prev.filter(r => r.id !== id);
          return [...filtered, { id, oldKey: originalKey.trim(), newKey: newValue.trim() }];
        });
      } else if (originalKey && originalKey.trim() !== "" && 
                 originalKey.trim() === newValue.trim()) {
        
        setRenamedKeys(prev => prev.filter(r => r.id !== id));
      }
    }

    const scrollToCell = (callback) => {
      const rowNode = api.getDisplayedRowAtIndex(rowIndex);
      const totalRowCount = api.getDisplayedRowCount();
      
      if (rowNode) {
        let scrollPosition = 'middle';
        
        if (rowIndex >= totalRowCount - 3) {
          scrollPosition = 'bottom';
        }
        else if (rowIndex <= 2) {
          scrollPosition = 'top';
        }
        
        api.ensureNodeVisible(rowNode, scrollPosition);
        
        api.ensureIndexVisible(rowIndex, scrollPosition);
        
        if (rowIndex === totalRowCount - 1) {
          setTimeout(() => {
            api.ensureIndexVisible(rowIndex, 'bottom');
          }, 50);
        }
        
        setTimeout(() => {
          if (callback) callback();
        }, 200); 
      } else {
        const scrollPosition = rowIndex >= (api.getDisplayedRowCount() - 3) ? 'bottom' : 'middle';
        api.ensureIndexVisible(rowIndex, scrollPosition);
       
        if (callback) callback();
      }
    };

    const selectRow = () => {
      api.forEachNode((node) => {
        if (node?.data?.id === data.id) {
          node.setSelected(true);
          // api.ensureNodeVisible(node, 'middle');
          api.ensureIndexVisible(rowIndex, rowIndex === (api.getDisplayedRowCount() - 1) ? 'bottom' : 'middle');
        }
      });
    };

    scrollToCell(selectRow);

    const inputData = [{ ...data }];

    // Transform and update keyData
    const transformedData = transformData(inputData, keyData);
    console.log("transformedData:", transformedData);
    setKeyData(transformedData); 
  };

  const applyChanges = async () => {
    setDisableApplyBtn(true);
    
    if (renamedKeys.length > 0) {
      try {
        for (const renameItem of renamedKeys) {
          const renameResponse = await dispatch(
            api.endpoints.renameTradingPartnerKey.initiate({
              account,
              new_tpkey: renameItem.newKey,
              old_tpkey: renameItem.oldKey
            }, { forceRefetch: true })
          );
          
          if (renameResponse?.data?.current_state !== "SUCCESS") {
            setError(renameResponse?.data?.message || renameResponse?.data?.data?.[0] || t('BOX_MSG_GENERAL_ERROR'));
            setSeverity('error');
            setDisableApplyBtn(false);
            return;
          }
        }
        
        // Update originalKeys map after successful rename
        const updatedOriginalKeys = new Map(originalKeys);
        renamedKeys.forEach(renameItem => {
          updatedOriginalKeys.set(renameItem.id, renameItem.newKey);
        });
        setOriginalKeys(updatedOriginalKeys);
        setRenamedKeys([]);
      } catch (error) {
        setError(error.message || t("BOX_MSG_GENERAL_ERROR"));
        setSeverity('error');
        setDisableApplyBtn(false);
        return;
      }
    }
    
    if (keyData?.data?.trading_partner_key_details.length > 0) {
      try {
        // Filter out only old key names from renamed keys
        // Keep new key names so checkbox changes (searchable/unique) can be saved
        const renamedOldKeyNames = new Set(renamedKeys.map(r => r.oldKey));
        
        const filteredKeyData = {
          ...keyData,
          data: {
            ...keyData.data,
            trading_partner_key_details: keyData.data.trading_partner_key_details.filter(
              detail => !renamedOldKeyNames.has(detail.key)
            )
          }
        };
        // Only call save API if there are non-renamed changes
        if (filteredKeyData.data.trading_partner_key_details.length > 0) {
          const response = await dispatch(
            api.endpoints.saveTradingPartnerKeyDetails.initiate({
              account,
              data: filteredKeyData, 
            })
          );
          if (response?.data?.current_state === "SUCCESS") {
            setError(response?.data?.data?.trading_partner_keys || (renamedKeys.length > 0 ? t('SUCCESS') : ''))
            setSeverity('success')
            // Reset keyData after successful save
            setKeyData({ data: { trading_partner_key_details: [] } });
            setRenamedKeys([]);
            // Refresh the grid data
            // getTradingPartnerKeysDetail();
            return;
          } else if(response?.data?.current_state === "ERROR"){
             setError(response?.data?.data[0] || response?.data?.message || t('BOX_MSG_GENERAL_ERROR'))
             setSeverity('error')
            //  getTradingPartnerKeysDetail();
          } else {
            setError(response?.data?.message || t("BOX_MSG_GENERAL_ERROR"))
            setSeverity('error')
          }
        } else if (renamedKeys.length > 0) {
          // Only renames were made, show success
          setError(t('SUCCESS'));
          setSeverity('success');
          setKeyData({ data: { trading_partner_key_details: [] } });
          setRenamedKeys([]);
          return;
        }
       
        // setDisableApplyBtn(false);
      } catch (error) {
        setError(error.message || t("BOX_MSG_GENERAL_ERROR"))
        setSeverity('error')
        setDisableApplyBtn(false);
      }
    } 
  };

  // Delete Trading Partner Keys
  const deleteTPKey = async (keyName) => {
    if (!keyName) return false;
    try {
      const response = await dispatch(api.endpoints.deleteTradingPartnerKey.initiate({
        account,
        tp_key_name: encodeURIComponent(keyName)
      }, { forceRefetch: true }));
      
      if (response?.data?.current_state === 'SUCCESS') {
        setError(t('TP_KEY_DEL_SUCCESS'))
        setSeverity('success')
        return true;
      }
      setError(response?.message || t('TP_KEY_DEL_FAIL'))
      setSeverity('error')
      return false;
    }
    catch (error) {
      setError(error.message || t('BOX_MSG_GENERAL_ERROR'));
      setSeverity('error')
      return false;
    }
  };
  const getTradingPartners = async () => {
    try {
      const response = await dispatch(
        api.endpoints.getTradingPatners.initiate(
          { account },
          { forceRefetch: true }
        )
      ).unwrap();

      if (response?.current_state === "SUCCESS") {
        const data = response?.data?.trading_partners;
        return data;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  
  // Get Trading Partner key details
  const getTradingPartnerKeysDetail = async () => {
    const convertToObjectArray = (details) => {
      const sortedKeys = Object.keys(details)
        .filter((key) => key !== '')
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

      return sortedKeys.map((key, index) => ({
        id: index,
        key: key,
        unique: details[key].unique,
        searchable: details[key].searchable
      }));
    };

    setTPKeysLoading(true);
    try {
      const defaultTP = await getTradingPartners();
      const response = await dispatch(
        api.endpoints.getTradingPartnerKeysDetails.initiate({
          account,
          tp_name: encodeURIComponent(tpName || defaultTP[0])
        }, { forceRefetch: true })
      );
      if (response?.error) {
        setError(t('TP_KEY_ERR'));
        setSeverity('error')
      }
      if (response?.data?.current_state === "SUCCESS") {
        const keys = response?.data?.data?.trading_partner_details;
        const rows = convertToObjectArray(keys);
        
        // Store original keys for rename detection
        const originalKeysMap = new Map();
        rows.forEach(row => {
          if (row.key && row.key.trim() !== "") {
            originalKeysMap.set(row.id, row.key);
          }
        });
        setOriginalKeys(originalKeysMap);
        setRenamedKeys([]); // Reset renamed keys when loading new data
        
        if (rows) {
          if (userPermissions.includes("TRADING_PARTNER_RIGHTS")) {
            rows.push({
              id: rows.length,
              key: "",
              searchable: "",
              unique: "",
            });
          }
          setRowData(rows);
        } else {
          setRowData([{ id: 0, key: "", searchable: "", unique: "" }]);
          setError(t('NO_TP_KEY'));
          setSeverity('error')
        }

        // Initialize keyData based on fetched data
        setKeyData({
          data: {
            trading_partner_key_details: convertToObjectArray(keys)
          }
        });
      } else {
        setRowData([{ id: 0, key: "", searchable: "", unique: "" }]);
      }
    } catch (error) {
      setError(t('BOX_MSG_GENERAL_ERROR'));
      setSeverity('error')
    } finally {
      setTPKeysLoading(false);
    }
  };

  useEffect(() => {
    getTradingPartnerKeysDetail();
  }, []);

  const handleCancelClick = () => {
    setDisableApplyBtn(true)
    setTPKeyScreen(false);
}
  return (
    <>
      <div className="pdc-left-inner">
        <div className="font-12 padding-12">
          <p style={{ fontSize: "18px", fontWeight: "400" }}>
            {t("TAB_TRADE_PARTNER_KEYS")}
          </p>
          <p>{t("TP_KEYS_DESC")}</p>
        </div>
        {tpKeysLoading ? (
          <div
            className="pdc-container"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div style={{ height: "calc(100vh - 230px)", padding: "12px" }}>
              <CommonGrid
                identifier={'trading_partner_keys'}
                rowData={rowData}
                gridOptions={gridOptions}
                getGridAPI={getGridAPI}
                containerStyle={{
                  height: "100%",
                }}
                gridProps={{
                  ensureDomOrder: true,
                  onCellValueChanged: handleCellValueChanged
                }}
                rowSelection="single"
              />
            </div>
            <div className="flex" style={{ justifyContent: 'flex-end', alignItems: 'center', padding: '5px', paddingRight: '10px', gap: '5px' }}>
              <Button size="small" variant='outlined' disabled={disableApplyBtn} onClick={applyChanges}> {t('APPLY')} </Button>
              <LightTooltip arrow title = {'Back to the Trading Partner screen'}>
                <Button size='small' variant="outlined" onClick={handleCancelClick}> {t('BTN_CANCEL')} </Button>
              </LightTooltip>
            </div>
          </>
        )}
      </div>
      {error && (
        <NtwAlert
          open={Boolean(error)}
          message={error}
          type={severity}
          hideDuration={10000}
          onClose={() => setError("")}
        />
      )}
      {errorModal && (
        <ErrorModal 
          errorModal={errorModal}
          errorMessage={errorMessage}
          OkHandler={() => {
            setErrorModal(false);
          }}
        />
      )}
    </>
  );
}

export default TradingPartnerKeys;
